import React from 'react';
import { cx } from '@linaria/atomic';
import { classNames } from '@frontend/string';
import { useResponsiveModal } from '../modal/responsive-modal/responsive-modal.context';
import { MultiStepStepper, MultiStepStepperProps } from './atoms/stepper/stepper';
import { useMultiStepContext } from './multi-step.context';
import { MultiStepControl } from './multi-step.hooks';
import MultiStepProvider, { MultiStepProviderRef } from './multi-step.provider';
import {
  body,
  bodyPaddingCompact,
  bodyPaddingFull,
  bodyPaddingNone,
  root,
  rootContainer,
  rootContainerResponsive,
  rootInModal,
  rootNotInModal,
  rootRounded,
} from './multi-step.styles';

export type MultiStepProps = MultiStepControl &
  React.PropsWithChildren<{
    components?: Partial<{
      /**
       * A method accepts stepper props and returns a React node.
       * Use to select a different stepper, or to use default stepper but override
       * the props that are bound by default.
       */
      Stepper: (props?: MultiStepStepperProps) => React.ReactNode | null;
    }>;
  }>;

export type MultiStepRefProps = {
  className?: string;
  style?: React.CSSProperties;
};

export const MultiStepRoot = React.forwardRef<MultiStepProviderRef, MultiStepProps & MultiStepRefProps>(
  (
    { className, style, containerRef, components = {}, children, isResponsiveMode: isResponsiveModeProp, ...props },
    ref
  ) => {
    const { Stepper = MultiStepStepper } = components;

    const { isResponsiveMode: isResponsiveModeContext } = useResponsiveModal();

    /**
     * If `isResponsiveMode` prop is provided, it takes precedence over the context value.
     */
    const isResponsiveMode = isResponsiveModeProp ?? isResponsiveModeContext;
    return (
      <div
        ref={containerRef}
        className={cx(rootContainer, isResponsiveMode && rootContainerResponsive, 'multi-step__root-container')}
      >
        <MultiStepProvider ref={ref} containerRef={containerRef} isResponsiveMode={isResponsiveMode} {...props}>
          <MultiStepBody
            className={classNames('multi-step__body-wrapper ', className)}
            style={{ height: '100%', ...style }}
            stepper={<Stepper />}
          >
            {children}
          </MultiStepBody>
        </MultiStepProvider>
      </div>
    );
  }
);

MultiStepRoot.displayName = 'MultiStep';

function MultiStepBody({
  stepper,
  children,
  className,
  style,
}: React.PropsWithChildren<{
  stepper: React.ReactNode;
}> &
  MultiStepRefProps) {
  const {
    isInModal,
    isResponsiveMode,
    stepper: { show, display },
  } = useMultiStepContext();

  const showStepper = show && !isResponsiveMode;

  return (
    <div
      className={cx(
        root,
        isInModal ? rootInModal : rootNotInModal,
        !isInModal && !isResponsiveMode && rootRounded,
        className
      )}
      style={style}
    >
      {showStepper && stepper}
      <div
        className={cx(
          'multi-step__body',
          body,
          showStepper ? (display !== 'full' ? bodyPaddingCompact : bodyPaddingFull) : bodyPaddingNone
        )}
      >
        {children}
      </div>
    </div>
  );
}

MultiStepBody.displayName = 'MultiStepBody';
